<script setup lang="ts">
import { from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { HeadObjectPlain, useHead } from '@vueuse/head';
import { computed, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';

import Breadcrumb from '@/components/base/layout/Breadcrumb.vue';
import CustomTrayBuilder from '@/components/custom-trays/CustomTrayBuilder.vue';
import Summary from '@/components/custom-trays/Summary.vue';
import DyBanner from '@/components/dynamic-yield/DyBanner.vue';
import ImageGallery from '@/components/pdp/legacy/image-gallery/ImageGallery.vue';
import { useDyChooseResults } from '@/composables/dynamic-yield/experiences';
import { MEDIUM_CUSTOM_TRAY_PRODUCT_KEY, MEDIUM_CUSTOM_TRAY_SKU } from '@/lib/customizers/trays';
import { ContentChoice } from '@/lib/personalization/dynamicYield';
import { sendProductViewedEvent } from '@/utils/analytics/productViewedEvent';
import { toLegacySku } from '@/utils/product';

interface Filter {
  filter: string;
  name: string;
}

interface NestedFilters extends Filter {
  children: Filter[];
}

interface Ingredient {
  assetPath: string;
  filters: string[];
  /** product key */
  id: string;
  name: string;
  popularity: number;
  priceLi: number;
  priceLo: number;
  priceMi: number;
  priceMo: number;
  priceRange: '$' | '$$' | '$$$';
}

interface Sku {
  /** similar to `variantName`; e.g. `Approx. 3.5 lbs` */
  details: string;
  /** the variant sku; e.g. `9900-03` */
  externalId: string;
  isPurchaseAllowed: boolean;
  /** the product key; e.g. `9900` */
  itemKey: string;
  /** e.g. `Medium Custom Tray` */
  name: string;
}

interface Review {
  id: number;
  content: string;
  author: string;
  locality: string;
  date: string;
  helpfulScore: number;
  ratingClass: string | null;
  rating: number | null;
  size: string | null;
}

interface StarRating {
  percentage: number;
  count: number;
}

interface ReviewsData {
  averageRating: number;
  count: number;
  featuredReview: Review | null;
  limit: number;
  offset: number;
  reviews: Review[];
  starRatingsCount: {
    [key: string]: StarRating;
  };
  totalRatings: number;
  totalReviews: number;
}

export interface CustomTraysProps {
  ancestors: {
    name: string;
    url: string;
  }[];
  catalogCategoryTree: NestedFilters[];
  digitalGifting?: {
    digitalGiftSku: string;
    giftCertificateCode: string;
    giftedTray: {
      name: string;
      sku: string;
      tier: 'Classic' | 'Premium';
    };
    giftMessage: string;
    senderName: string;
  };
  eventLocation: string;
  ingredientCatalog: Ingredient[];
  metaData: HeadObjectPlain;
  name: string;
  reviews: ReviewsData;
  skus: [Sku, Sku];
}

const props = defineProps<CustomTraysProps>();

useHead({
  ...props.metaData,
  bodyAttrs: {
    class: ['page-pdp', 'page-custom-trays'],
  },
});

const imageUrl = '/images/custom-gifts/custom-tray-pdp.jpg';
/** default metadata value */
const sku = toLegacySku(MEDIUM_CUSTOM_TRAY_SKU);
const route = useRoute();
const productDetailEvent = () => {
  sendProductViewedEvent({
    eventLocation: props.eventLocation,
    name: 'Custom Trays',
    price: from(0),
    productId: MEDIUM_CUSTOM_TRAY_PRODUCT_KEY,
    sku: MEDIUM_CUSTOM_TRAY_SKU,
    unitName: 'Medium Custom Tray',
  });
};

const encodedIngredientIds = ref(route.query.code as string | undefined);

const router = useRouter();
const showModal = () => {
  router.push({ query: { code: encodedIngredientIds.value } });
};

const dySelector = '[Banner] Custom Tray - Recommendations';
const { choicesByName, loadExperiences } = useDyChooseResults<ContentChoice>(dySelector, route);
const isDigitalGifting = computed(() => !!Object.keys(props.digitalGifting ?? {}).length);
onMounted(() => {
  if (/^#[a-z0-9]{15}l?$/gi.test(route.hash)) {
    router.replace({ query: { code: route.hash.substring(1) } });
  }

  productDetailEvent();
  loadExperiences();

  if (isDigitalGifting.value) {
    showModal();
  }
});

onBeforeRouteLeave(() => {
  document.querySelector('body')!.style.overflow = 'visible';
});

watch(encodedIngredientIds, (newCode) => {
  const { code } = route.query;
  if (code && code !== newCode) {
    router.replace({ query: { code: newCode } });
  }
});

const visible = computed(() => !!route.query.code);
watch(visible, () => {
  const overflow = visible.value ? 'hidden' : 'visible';
  document.querySelector('body')!.style.overflow = overflow;
});

const giftDropLogoSvg = nutshell['static_img/digital-gift/gift-drop-logo.svg'];
const titleImage = ['large', 'medium', 'small', 'thumb', 'url', 'zoom'].reduce(
  (acc, key) => ({ ...acc, [key]: imageUrl }),
  {} as Record<string, string>,
);
</script>

<template>
  <div class="pt-4 page-content lg:pt-6" itemscope itemtype="http://schema.org/Product">
    <div class="container">
      <div>
        <meta itemprop="brand" content="Nuts.com" />
        <meta itemprop="name" :content="name" />
        <meta
          itemprop="description"
          content="Build your own gift! Customize with 150+ different options of nuts,
            dried fruit, chocolates and more."
        />
        <meta
          itemprop="image"
          content="https://ed910ae2d60f0d25bcb8-80550f96b5feb12604f4f720bfefb46d.ssl.cf1.rackcdn.com/100ac60d8e43a63a-u8ZlH-nZ.jpg"
        />
        <meta itemprop="sku" :content="sku" />
        <meta itemprop="mpn" :content="sku" />
        <meta itemprop="url" :content="route.path" />
        <div itemprop="offers" itemscope itemtype="http://schema.org/AggregateOffer">
          <meta itemprop="sku" :content="sku" />
          <meta itemprop="lowPrice" content="39.99" />
          <meta itemprop="highPrice" content="179.99" />
          <meta itemprop="offerCount" content="2" />
          <meta itemprop="priceCurrency" content="USD" />
          <meta itemprop="priceValidUntil" content="2200-01-01" />
          <meta itemprop="itemCondition" href="http://schema.org/NewCondition" />
          <meta itemprop="availability" content="InStock" />
          <meta itemprop="url" :content="route.path" />
        </div>
        <div
          v-if="reviews.averageRating"
          itemprop="aggregateRating"
          itemscope
          itemtype="http://schema.org/AggregateRating"
        >
          <meta itemprop="ratingValue" :content="reviews.averageRating" />
          <meta itemprop="bestRating" content="5" />
          <meta itemprop="reviewCount" :content="reviews.totalReviews" />
        </div>
        <div
          v-for="(review, i) of reviews.reviews"
          :key="i"
          itemprop="review"
          itemscope="itemscope"
          itemtype="http://schema.org/Review"
        >
          <div
            v-if="review.rating"
            itemprop="reviewRating"
            itemscope="itemscope"
            itemtype="https://schema.org/Rating"
          >
            <meta itemprop="ratingValue" :content="review.rating" />
            <meta itemprop="bestRating" content="5" />
          </div>
          <meta itemprop="dateCreated" :content="review.date" />
          <meta itemprop="reviewBody" :content="review.content" />
          <div itemprop="author" itemscope="itemscope" itemtype="https://schema.org/Person">
            <meta itemprop="name" :content="review.author" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div v-once id="dy-hook-above-breadcrumbs-recs" />
          <Breadcrumb :ancestors class="pb-1 ws-md:pb-5" />
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 col-md-12">
          <div>
            <h1 class="font-semibold" itemprop="name">{{ name }}</h1>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 col-md-8">
          <div id="section" class="mb-20">
            <ImageGallery :images="[titleImage]" productName="Custom Trays" :titleImage />
          </div>
        </div>
        <div class="col-sm-12 col-md-4">
          <div class="section">
            <Summary @show-custom-tray-builder="showModal" />
            <CustomTrayBuilder
              v-model:encodedIngredientIds="encodedIngredientIds"
              :catalogCategoryTree
              :currentCode="route.query.code"
              :digitalGifting
              :ingredients="ingredientCatalog"
              :isDigitalGifting
              :skus
              :visible
              @hide-custom-tray-builder="router.push({ query: {} })"
            />
          </div>
        </div>
      </div>

      <RouterLink
        to="/gift-drop"
        class="flex flex-col justify-center px-10 py-6 mb-6 rounded-lg cursor-pointer bg-nuts-orange-50 col-sm-12 col-md-12 md:flex-row hover:no-underline"
      >
        <img :src="giftDropLogoSvg" class="mb-2 md:mb-0" alt="GiftDrop" />
        <div class="text-center md:text-left md:ml-14">
          <div class="flex flex-col mb-2 text-sm font-semibold text-black md:text-xl md:flex-row">
            Let them choose their favorites with GiftDrop!
          </div>
          <div class="text-sm text-black md:text-base">
            No guesswork or mailing address required.
            <span class="underline">Learn&nbsp;More</span>
          </div>
        </div>
      </RouterLink>
    </div>

    <DyBanner :banner="choicesByName[dySelector]" containerId="recommended-trays" />
  </div>
</template>

<style lang="scss" scoped>
h1 {
  @include respond-max($screen-xs-max) {
    font-size: 26px;
  }
}
</style>
